import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookMarked, Pencil, Trash2, Settings2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export const PromptLibrary = ({ onUse }) => {
  const [prompts, setPrompts] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");

  const load = useCallback(() => {
    api.get("/prompts").then(({ data }) => setPrompts(data.prompts));
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => { setEditing(p); setTitle(p.title); setText(p.prompt); };
  const startNew = () => { setEditing({ id: null }); setTitle(""); setText(""); };

  const save = async () => {
    if (!title.trim() || !text.trim()) return toast.error("Title and prompt are required");
    if (editing.id) {
      await api.put(`/prompts/${editing.id}`, { title, prompt: text });
      toast.success("Prompt updated");
    } else {
      await api.post("/prompts", { title, prompt: text });
      toast.success("Prompt created");
    }
    setEditing(null);
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete prompt "${p.title}"?`)) return;
    await api.delete(`/prompts/${p.id}`);
    toast.success("Prompt deleted");
    load();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="prompt-library-button">
            <BookMarked className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Report Templates
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {prompts.map((p) => (
            <DropdownMenuItem key={p.id} onClick={() => onUse(p.prompt)} data-testid={`prompt-use-${p.id}`}>
              {p.title}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setManageOpen(true)} data-testid="prompt-manage-button">
            <Settings2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" /> Manage templates
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Report Templates</DialogTitle>
            <DialogDescription>Pre-made prompts for one-click reports from Claude.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <div className="space-y-3">
              <Input placeholder="Template title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="prompt-title-input" />
              <Textarea placeholder="Prompt text…" rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="prompt-text-input" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(null)} data-testid="prompt-edit-cancel">Cancel</Button>
                <Button size="sm" onClick={save} data-testid="prompt-save-button">Save</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {prompts.map((p) => (
                <div key={p.id} className="flex items-start gap-2 border border-border rounded-md p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{p.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{p.prompt}</p>
                  </div>
                  <button className="text-muted-foreground hover:text-foreground" onClick={() => startEdit(p)} data-testid={`prompt-edit-${p.id}`} aria-label="Edit">
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <button className="text-muted-foreground hover:text-red-600" onClick={() => remove(p)} data-testid={`prompt-delete-${p.id}`} aria-label="Delete">
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <Button size="sm" className="w-full" onClick={startNew} data-testid="prompt-new-button">+ New template</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
